import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Search, Eye, RotateCcw, ArrowLeft, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort, formatDateFromIso } from '@/lib/datetime';

const statusLabels = {
    pending: 'Pendiente',
    completed: 'Completado',
    failed: 'Fallido',
    refunded: 'Reembolsado',
};

const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    completed: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
    refunded: 'bg-blue-500/20 text-blue-400',
};

const methodLabels = {
    cash: 'Efectivo',
    mercadopago: 'MercadoPago',
};

export default function AdminSubscriptions() {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [refunding, setRefunding] = useState(false);

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/payments', {
                params: { search, status: statusFilter, method: methodFilter, page, per_page: 15 },
            });
            setPayments(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, statusFilter, methodFilter, page]);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    const openDetail = (payment) => {
        setSelectedPayment(payment);
        setDetailOpen(true);
    };

    const handleRefund = async (payment) => {
        if (!confirm(`¿Reembolsar pago de $${payment.amount} de ${payment.gym?.owner?.name || payment.gym?.name}?`)) return;
        setRefunding(true);
        try {
            const res = await api.post(`/admin/payments/${payment.id}/refund`);
            setSelectedPayment(res.data.data);
            fetchPayments();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al reembolsar.');
        } finally { setRefunding(false); }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide">Suscripciones y Pagos</h1>
            </div>

            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input
                            placeholder="Buscar por referencia, owner, gym..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                            className="max-w-sm"
                        />
                        <select
                            className="h-9 rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 text-sm text-white"
                            value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                        >
                            <option value="">Todos los estados</option>
                            <option value="pending">Pendiente</option>
                            <option value="completed">Completado</option>
                            <option value="failed">Fallido</option>
                            <option value="refunded">Reembolsado</option>
                        </select>
                        <select
                            className="h-9 rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 text-sm text-white"
                            value={methodFilter}
                            onChange={e => { setMethodFilter(e.target.value); setPage(1); }}
                        >
                            <option value="">Todos los métodos</option>
                            <option value="mercadopago">MercadoPago</option>
                            <option value="cash">Efectivo</option>
                        </select>
                        <span className="text-sm text-white/60 ml-auto">{meta.total ?? 0} pagos</span>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : payments.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron pagos.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[700px]">
                                    <thead>
                                        <tr className="border-b border-hairline-dark">
                                            <th className="text-left py-3 px-2 font-medium">ID</th>
                                            <th className="text-left py-3 px-2 font-medium">Propietario</th>
                                            <th className="text-left py-3 px-2 font-medium">Gimnasio</th>
                                            <th className="text-left py-3 px-2 font-medium">Cliente</th>
                                            <th className="text-right py-3 px-2 font-medium">Monto</th>
                                            <th className="text-left py-3 px-2 font-medium">Método</th>
                                            <th className="text-left py-3 px-2 font-medium">Estado</th>
                                            <th className="text-left py-3 px-2 font-medium">Fecha</th>
                                            <th className="text-right py-3 px-2 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map(p => (
                                            <tr key={p.id} className="border-b border-hairline-dark last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 font-mono text-xs text-white/60">#{p.id}</td>
                                                <td className="py-3 px-2 font-medium">{p.gym?.owner?.name || '-'}</td>
                                                <td className="py-3 px-2">{p.gym?.name || '-'}</td>
                                                <td className="py-3 px-2">{p.customer?.name || '-'}</td>
                                                <td className="py-3 px-2 text-right font-mono">${parseFloat(p.amount).toFixed(2)}</td>
                                                <td className="py-3 px-2">{methodLabels[p.method] || p.method}</td>
                                                <td className="py-3 px-2">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[p.status] || 'bg-white/10 text-white'}`}>
                                                        {statusLabels[p.status] || p.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-2 text-white/60 text-xs">
                                                    {formatDateFromIso(p.paid_at || p.created_at)}
                                                </td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button variant="outline" size="icon" onClick={() => openDetail(p)} title="Ver detalle"><Eye className="h-4 w-4" /></Button>
                                                        {p.method === 'mercadopago' && p.status !== 'refunded' && p.status !== 'pending' && (
                                                            <Button variant="outline" size="icon" onClick={() => handleRefund(p)} disabled={refunding} title="Reembolsar"><RotateCcw className="h-4 w-4" /></Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center items-center gap-3 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                        <ArrowLeft className="h-4 w-4 mr-1" />Anterior
                                    </Button>
                                    <span className="text-sm text-white/60">
                                        Página {meta.current_page} de {meta.last_page}
                                    </span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)}>
                                        Siguiente<ArrowRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Detalle del Pago</DialogTitle>
                    </DialogHeader>
                    {selectedPayment && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">ID</Label>
                                    <p className="font-mono text-white mt-1">#{selectedPayment.id}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Estado</Label>
                                    <p className="mt-1">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedPayment.status] || 'bg-white/10 text-white'}`}>
                                            {statusLabels[selectedPayment.status] || selectedPayment.status}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Propietario</Label>
                                    <p className="text-white mt-1">{selectedPayment.gym?.owner?.name || '-'}</p>
                                    <p className="text-white/60 text-xs">{selectedPayment.gym?.owner?.email || ''}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Gimnasio</Label>
                                    <p className="text-white mt-1">{selectedPayment.gym?.name || '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Cliente</Label>
                                    <p className="text-white mt-1">{selectedPayment.customer?.name || '-'}</p>
                                    {selectedPayment.customer?.dni && (
                                        <p className="text-white/60 text-xs">{selectedPayment.customer.dni}</p>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Monto</Label>
                                    <p className="text-white font-mono mt-1">${parseFloat(selectedPayment.amount).toFixed(2)}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Método</Label>
                                    <p className="text-white mt-1 capitalize">{methodLabels[selectedPayment.method] || selectedPayment.method}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Plan</Label>
                                    <p className="text-white mt-1">{selectedPayment.subscription?.plan?.name || '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Pagado el</Label>
                                    <p className="text-white mt-1">
                                        {formatDateTimeShort(selectedPayment.paid_at)}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Referencia</Label>
                                    <p className="font-mono text-white/80 text-xs mt-1 break-all">{selectedPayment.reference || '-'}</p>
                                </div>
                                {selectedPayment.gateway_payment_id && (
                                    <div className="col-span-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">ID MercadoPago</Label>
                                        <p className="font-mono text-white/80 text-xs mt-1">{selectedPayment.gateway_payment_id}</p>
                                    </div>
                                )}
                                {selectedPayment.notes && (
                                    <div className="col-span-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Notas</Label>
                                        <p className="text-white/80 text-sm mt-1">{selectedPayment.notes}</p>
                                    </div>
                                )}
                            </div>
                            {selectedPayment.method === 'mercadopago' && selectedPayment.status !== 'refunded' && selectedPayment.status !== 'pending' && (
                                <div className="pt-2 border-t border-hairline-dark">
                                    <Button
                                        onClick={() => { setDetailOpen(false); handleRefund(selectedPayment); }}
                                        disabled={refunding}
                                        variant="outline"
                                        className="w-full"
                                    >
                                        <RotateCcw className="h-4 w-4 mr-2" />
                                        {refunding ? 'Reembolsando...' : 'Reembolsar pago'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDetailOpen(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
